import { useEffect, useState } from 'react';
import { Badge, Button, SidePanel, useToast } from '../../../ui';
import { supabase } from '../../../../lib/supabase';
import { faint, fmtCompact, fmtDate, linkStyle, textareaStyle } from './format';
import { DRBadge, Field, MetricRow, PanelSection, ReasonField } from './shared';

type Backlink = {
  id: string;
  source_url: string;
  target_url: string;
  anchor_text: string | null;
  link_type: string | null;
  source_domain: string;
  source_dr: number | null;
  source_ur: number | null;
  source_traffic: number | null;
  first_seen: string | null;
  last_seen: string | null;
  is_live: boolean;
  hidden: boolean;
  hidden_reason: string | null;
  notes: string | null;
  status: string | null;
};

interface BacklinkDetailPanelProps {
  backlinkId: string;
  onClose: () => void;
  onUpdate: () => void;
  onHide: (id: string, reason: string) => void;
  onUnhide: (id: string) => void;
}

export default function BacklinkDetailPanel({ backlinkId, onClose, onUpdate, onHide, onUnhide }: BacklinkDetailPanelProps) {
  const toast = useToast();
  const [backlink, setBacklink] = useState<Backlink | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [hiding, setHiding] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { data, error } = await supabase.from('client_backlinks').select('*').eq('id', backlinkId).single();
      if (error) {
        console.error('Error loading backlink:', error);
        toast.error('Failed to load backlink');
        return;
      }
      setBacklink(data);
      setNotes(data.notes || '');
    };
    load();
  }, [backlinkId, toast]);

  const handleSaveNotes = async () => {
    if (!backlink) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('client_backlinks').update({ notes }).eq('id', backlink.id);
      if (error) throw error;
      toast.success('Notes saved');
      onUpdate();
    } catch (err) {
      console.error('Error saving notes:', err);
      toast.error('Failed to save notes');
    } finally {
      setSaving(false);
    }
  };

  const notesDirty = !!backlink && notes !== (backlink.notes || '');

  return (
    <SidePanel
      open
      onClose={onClose}
      width={520}
      eyebrow="Backlink"
      title={backlink?.source_domain ?? 'Loading…'}
      footer={
        backlink ? (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {hiding ? (
              <ReasonField
                placeholder="Why hide this link?"
                confirmLabel="Hide"
                onConfirm={(reason) => {
                  onHide(backlink.id, reason);
                  onClose();
                }}
                onCancel={() => setHiding(false)}
              />
            ) : (
              <>
                {backlink.hidden ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      onUnhide(backlink.id);
                      onClose();
                    }}
                  >
                    Unhide
                  </Button>
                ) : (
                  <Button variant="ghost" onClick={() => setHiding(true)}>
                    Hide link
                  </Button>
                )}
                <span style={{ flex: 1 }} />
                <Button variant="primary" onClick={handleSaveNotes} disabled={saving || !notesDirty}>
                  {saving ? 'Saving…' : 'Save notes'}
                </Button>
              </>
            )}
          </div>
        ) : null
      }
    >
      {backlink && (
        <>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: 'var(--space-5)' }}>
            {backlink.link_type === 'dofollow' ? <Badge tone="up">Dofollow</Badge> : <Badge tone="neutral">{backlink.link_type || 'Unknown'}</Badge>}
            <Badge tone={backlink.is_live ? 'up' : 'down'} dot>
              {backlink.is_live ? 'Live' : 'Lost'}
            </Badge>
            {backlink.hidden && <Badge tone="idle">Hidden</Badge>}
          </div>

          <MetricRow
            items={[
              { label: 'DR', value: <DRBadge dr={backlink.source_dr} /> },
              { label: 'UR', value: backlink.source_ur != null ? Math.round(backlink.source_ur) : '—' },
              { label: 'Traffic', value: fmtCompact(backlink.source_traffic) },
            ]}
          />

          {backlink.hidden && backlink.hidden_reason && (
            <div
              style={{
                padding: 'var(--space-3) var(--space-4)',
                background: 'var(--surface-raised)',
                borderRadius: 'var(--radius-md)',
                marginBottom: 'var(--space-6)',
                fontSize: 'var(--text-sm)',
              }}
            >
              <span style={faint}>Hidden because </span>
              {backlink.hidden_reason}
            </div>
          )}

          <PanelSection title="Link">
            <Field label="From">
              <a href={backlink.source_url} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                {backlink.source_url}
              </a>
            </Field>
            <Field label="To">
              <a href={backlink.target_url} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                {backlink.target_url}
              </a>
            </Field>
            <Field label="Anchor">{backlink.anchor_text || <span style={faint}>No anchor</span>}</Field>
            <Field label="First seen">{fmtDate(backlink.first_seen)}</Field>
            <Field label="Last seen">{fmtDate(backlink.last_seen)}</Field>
          </PanelSection>

          <PanelSection title="Notes">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything worth remembering about this link" style={textareaStyle} />
          </PanelSection>
        </>
      )}
    </SidePanel>
  );
}
